import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { HTTPError } from '../../../gen/client/react'
import type { MeProfileResp, MeUpdateProfileReq } from '../../../gen/client/react'
import {
  useAdminGenClient,
  useAdminGenClientNoAutoLogout,
} from '../api/genClient'
import {
  CrudForm,
  CrudTable,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'

// ---------------------------------------------------------------------------
// Wire shapes
//
// MeEmailsResp.emails / MeAppPasswordsResp.appPasswords are typed as
// Array<Record<string, unknown>> on the generated client (the OpenAPI schema
// backing these two responses declares each entry as a free-form object —
// see selfservice_emails.go's meEmailWire / selfservice_app_passwords.go's
// appPasswordWire for the actual server-side shape). The generated client's
// decodeJSON() still runs every response body through fromWire() regardless
// of the declared TS type, so the raw JS values ARE camelCase by the time
// they reach this component — these narrow, defensive readers just give
// that untyped shape a name instead of scattering `as` casts through the
// render code.
// ---------------------------------------------------------------------------

interface ProfileEmail {
  id: string
  email: string
  verified: boolean
  primary: boolean
  createdAt?: string
}

function toProfileEmail(raw: Record<string, unknown>): ProfileEmail | null {
  if (typeof raw.id !== 'string' || typeof raw.email !== 'string') return null
  return {
    id: raw.id,
    email: raw.email,
    verified: raw.verified === true,
    primary: raw.primary === true,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
  }
}

interface ProfileAppPassword {
  id: string
  label: string
  scopes: string[]
  createdAt?: string
  expiresAt?: string
  lastUsedAt?: string
}

function toProfileAppPassword(raw: Record<string, unknown>): ProfileAppPassword | null {
  if (typeof raw.id !== 'string' || typeof raw.label !== 'string') return null
  return {
    id: raw.id,
    label: raw.label,
    scopes: Array.isArray(raw.scopes) ? raw.scopes.filter((s): s is string => typeof s === 'string') : [],
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
    expiresAt: typeof raw.expiresAt === 'string' ? raw.expiresAt : undefined,
    lastUsedAt: typeof raw.lastUsedAt === 'string' ? raw.lastUsedAt : undefined,
  }
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function errMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function emailStatusLabel(row: ProfileEmail): string {
  if (row.primary) return 'Primary'
  if (row.verified) return 'Verified'
  return 'Pending verification'
}

// The server (selfservice_app_passwords.go's Task 15 gate) intentionally
// returns 401 to mean "fresh step-up (<=15min AAL2) required", not "your
// session is dead" — resolveSessionAssurance's freshness window is 900s.
// This must never be conflated with a generic error, and the call that can
// produce it (meCreateAppPassword) is routed through
// useAdminGenClientNoAutoLogout so the auto-logout client's onUnauthorized
// never fires and clears the caller's session out from under them.
function isStepUpRequired(e: unknown): boolean {
  return e instanceof HTTPError && e.status === 401
}

const STEP_UP_MESSAGE =
  'Step-up authentication required: creating an app password needs a fresh passkey / second-factor verification (within the last 15 minutes). Re-authenticate with your passkey or security key, then try again.'

// ---------------------------------------------------------------------------
// Styled atoms
// ---------------------------------------------------------------------------

const InfoText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

const ReadOnlyRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

const ReadOnlyLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const StatusMessage = styled.p`
  margin: 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}14`};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

// SECURITY: renders the plaintext app-password secret. Only ever fed from
// transient component state (newSecret) that is never written to
// localStorage/sessionStorage and is cleared on dismiss/unmount — see
// handleCreateAppPassword and the cleanup effect below.
const SecretCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border: 2px solid ${({ theme }) => asUnified(theme).state?.warning ?? '#92400e'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => `${asUnified(theme).state?.warning ?? '#92400e'}0d`};
`

const SecretWarning = styled.p`
  margin: 0;
  font-weight: 700;
  color: ${({ theme }) => asUnified(theme).state?.warning ?? '#92400e'};
`

const SecretValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  flex-wrap: wrap;
`

const SecretCode = styled.code`
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  font-family: monospace;
  word-break: break-all;
`

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

const PROFILE_FIELDS: CrudField[] = [
  { name: 'givenName', label: 'Given name', type: 'text' },
  { name: 'familyName', label: 'Family name', type: 'text' },
  { name: 'name', label: 'Display name', type: 'text' },
  { name: 'picture', label: 'Picture URL', type: 'text' },
]

function profileInitial(profile: MeProfileResp | null): Record<string, unknown> {
  return {
    givenName: profile?.givenName ?? '',
    familyName: profile?.familyName ?? '',
    name: profile?.name ?? '',
    picture: profile?.picture ?? '',
  }
}

/**
 * Self-service profile / email / app-password management.
 *
 * Before this page there was ZERO profile UI in the admin app — the backend
 * (selfservice_profile.go / selfservice_emails.go /
 * selfservice_app_passwords.go) and the generated client methods were fully
 * implemented and simply never wired to any route. Mirrors
 * WebAuthnCredentials.tsx's hand-rolled per-section fetch/error/loading
 * pattern (useResource's generic list/create/remove doesn't fit: app-password
 * creation must surface the one-time secret to the caller, which
 * useResource's void-returning create() cannot do).
 */
export const Profile = (): JSX.Element => {
  const client = useAdminGenClient()
  // meCreateAppPassword alone routes through the no-auto-logout client: a
  // 401 there means "step-up required" (recoverable), not "session dead" —
  // see isStepUpRequired's doc comment above and genClient.ts's
  // useAdminGenClientNoAutoLogout doc comment.
  const noAutoLogoutClient = useAdminGenClientNoAutoLogout()

  // ---- Profile ----
  const [profile, setProfile] = useState<MeProfileResp | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null)
  const [profileFormKey, setProfileFormKey] = useState(0)

  const loadProfile = useCallback(async (): Promise<void> => {
    setProfileLoading(true)
    setProfileError(null)
    try {
      const resp = await client.meGetProfile({})
      setProfile(resp)
    } catch (e: unknown) {
      setProfileError(errMessage(e, 'Failed to load profile'))
    } finally {
      setProfileLoading(false)
    }
  }, [client])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const handleProfileSubmit = useCallback(async (values: Record<string, unknown>): Promise<void> => {
    setProfileSaveError(null)
    const req: MeUpdateProfileReq = {
      givenName: String(values.givenName ?? ''),
      familyName: String(values.familyName ?? ''),
      name: String(values.name ?? ''),
      picture: String(values.picture ?? ''),
    }
    try {
      const resp = await client.meUpdateProfile(req)
      setProfile(resp)
      setProfileFormKey((k) => k + 1)
    } catch (e: unknown) {
      setProfileSaveError(errMessage(e, 'Failed to save profile'))
    }
  }, [client])

  // ---- Emails ----
  const [emails, setEmails] = useState<ProfileEmail[]>([])
  const [emailsLoading, setEmailsLoading] = useState(true)
  const [emailsError, setEmailsError] = useState<string | null>(null)
  const [emailsBusyId, setEmailsBusyId] = useState<string | null>(null)
  const [addEmailMessage, setAddEmailMessage] = useState<string | null>(null)

  const loadEmails = useCallback(async (): Promise<void> => {
    setEmailsLoading(true)
    setEmailsError(null)
    try {
      const resp = await client.meListEmails({})
      setEmails((resp.emails ?? []).map(toProfileEmail).filter((e): e is ProfileEmail => e !== null))
    } catch (e: unknown) {
      setEmailsError(errMessage(e, 'Failed to load email addresses'))
    } finally {
      setEmailsLoading(false)
    }
  }, [client])

  useEffect(() => {
    loadEmails()
  }, [loadEmails])

  const handleAddEmail = useCallback(async (values: Record<string, unknown>): Promise<void> => {
    setEmailsError(null)
    setAddEmailMessage(null)
    const email = String(values.email ?? '').trim()
    if (!email) return
    try {
      await client.meAddEmail({ email })
      setAddEmailMessage(
        `A verification email was sent to ${email}. It will appear below once you confirm it.`
      )
      await loadEmails()
    } catch (e: unknown) {
      setEmailsError(errMessage(e, 'Failed to add email address'))
    }
  }, [client, loadEmails])

  const handleDeleteEmail = useCallback(async (emailId: string): Promise<void> => {
    setEmailsBusyId(emailId)
    setEmailsError(null)
    try {
      await client.meDeleteEmail({ emailId })
      setEmails((prev) => prev.filter((e) => e.id !== emailId))
    } catch (e: unknown) {
      setEmailsError(errMessage(e, 'Failed to remove email address'))
    } finally {
      setEmailsBusyId(null)
    }
  }, [client])

  const handleSetPrimaryEmail = useCallback(async (emailId: string): Promise<void> => {
    setEmailsBusyId(emailId)
    setEmailsError(null)
    try {
      await client.meSetPrimaryEmail({ emailId })
      await loadEmails()
    } catch (e: unknown) {
      setEmailsError(errMessage(e, 'Failed to set primary email address'))
    } finally {
      setEmailsBusyId(null)
    }
  }, [client, loadEmails])

  const emailColumns: CrudColumn<ProfileEmail>[] = [
    { key: 'email', header: 'Email' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => emailStatusLabel(row),
    },
    { key: 'createdAt', header: 'Added', render: (row) => formatDate(row.createdAt) },
  ]

  const emailRowActions = (row: ProfileEmail): React.ReactNode => {
    if (row.primary) return null
    const busy = emailsBusyId === row.id
    return (
      <CrudActionsRow>
        {row.verified && (
          <CrudSecondaryButton
            type="button"
            disabled={busy}
            onClick={() => handleSetPrimaryEmail(row.id)}
          >
            Set primary
          </CrudSecondaryButton>
        )}
        <CrudDangerButton type="button" disabled={busy} onClick={() => handleDeleteEmail(row.id)}>
          Remove
        </CrudDangerButton>
      </CrudActionsRow>
    )
  }

  // ---- App passwords ----
  const [appPasswords, setAppPasswords] = useState<ProfileAppPassword[]>([])
  const [appPasswordsLoading, setAppPasswordsLoading] = useState(true)
  const [appPasswordsError, setAppPasswordsError] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [createAppPasswordError, setCreateAppPasswordError] = useState<string | null>(null)
  const [createAppPasswordFormKey, setCreateAppPasswordFormKey] = useState(0)
  // The one-time plaintext secret. NEVER persisted to storage, NEVER logged
  // — held only in this transient piece of component state, and explicitly
  // cleared both on dismiss (handleDismissSecret) and on unmount (the
  // cleanup effect below), per the "display exactly once" requirement.
  const [newSecret, setNewSecret] = useState<{ id: string; label: string; secret: string } | null>(null)
  const [copyStatus, setCopyStatus] = useState<string | null>(null)

  const loadAppPasswords = useCallback(async (): Promise<void> => {
    setAppPasswordsLoading(true)
    setAppPasswordsError(null)
    try {
      const resp = await client.meListAppPasswords({})
      setAppPasswords(
        (resp.appPasswords ?? []).map(toProfileAppPassword).filter((p): p is ProfileAppPassword => p !== null)
      )
    } catch (e: unknown) {
      setAppPasswordsError(errMessage(e, 'Failed to load app passwords'))
    } finally {
      setAppPasswordsLoading(false)
    }
  }, [client])

  useEffect(() => {
    loadAppPasswords()
  }, [loadAppPasswords])

  // Clear the one-time secret from memory when the user navigates away from
  // this page (component unmount) — belt-and-suspenders alongside the
  // explicit Dismiss button, per the "never persist / clear on navigate
  // away" requirement.
  useEffect(() => {
    return () => setNewSecret(null)
  }, [])

  const handleCreateAppPassword = useCallback(async (values: Record<string, unknown>): Promise<void> => {
    setCreateAppPasswordError(null)
    const label = String(values.label ?? '').trim()
    if (!label) return
    const expiresRaw = String(values.expiresInDays ?? '').trim()
    const expiresInDays = expiresRaw ? Number(expiresRaw) : undefined
    try {
      const resp = await noAutoLogoutClient.meCreateAppPassword({
        label,
        ...(expiresInDays !== undefined && !Number.isNaN(expiresInDays) ? { expiresInDays } : {}),
      })
      setNewSecret({ id: resp.id, label: resp.label, secret: resp.secret })
      setCreateAppPasswordFormKey((k) => k + 1)
      await loadAppPasswords()
    } catch (e: unknown) {
      setCreateAppPasswordError(isStepUpRequired(e) ? STEP_UP_MESSAGE : errMessage(e, 'Failed to create app password'))
    }
  }, [noAutoLogoutClient, loadAppPasswords])

  const handleRevokeAppPassword = useCallback(async (id: string): Promise<void> => {
    setRevokingId(id)
    setAppPasswordsError(null)
    try {
      await client.meRevokeAppPassword({ appPasswordId: id })
      setAppPasswords((prev) => prev.filter((p) => p.id !== id))
    } catch (e: unknown) {
      setAppPasswordsError(errMessage(e, 'Failed to revoke app password'))
    } finally {
      setRevokingId(null)
    }
  }, [client])

  const handleDismissSecret = useCallback((): void => {
    setNewSecret(null)
    setCopyStatus(null)
  }, [])

  const handleCopySecret = useCallback((): void => {
    if (!newSecret) return
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(newSecret.secret)
        .then(() => setCopyStatus('Copied to clipboard.'))
        .catch(() => setCopyStatus('Could not copy automatically — copy it manually.'))
    } else {
      setCopyStatus('Could not copy automatically — copy it manually.')
    }
  }, [newSecret])

  const appPasswordColumns: CrudColumn<ProfileAppPassword>[] = [
    { key: 'label', header: 'Label' },
    { key: 'scopes', header: 'Scopes', render: (row) => (row.scopes.length ? row.scopes.join(', ') : 'default') },
    { key: 'createdAt', header: 'Created', render: (row) => formatDate(row.createdAt) },
    { key: 'expiresAt', header: 'Expires', render: (row) => (row.expiresAt ? formatDate(row.expiresAt) : 'Never') },
    { key: 'lastUsedAt', header: 'Last used', render: (row) => (row.lastUsedAt ? formatDate(row.lastUsedAt) : 'Never') },
  ]

  const appPasswordRowActions = (row: ProfileAppPassword): React.ReactNode => (
    <CrudDangerButton
      type="button"
      disabled={revokingId === row.id}
      onClick={() => handleRevokeAppPassword(row.id)}
    >
      Revoke
    </CrudDangerButton>
  )

  const emailFields: CrudField[] = [
    { name: 'email', label: 'Add email address', type: 'text', required: true },
  ]
  const appPasswordFields: CrudField[] = [
    { name: 'label', label: 'Label', type: 'text', required: true },
    { name: 'expiresInDays', label: 'Expires in days (optional)', type: 'text' },
  ]

  return (
    <PageStack>
      <PageTitle>My Profile</PageTitle>

      <SectionBlock>
        <SectionTitle>Profile</SectionTitle>
        {profileLoading && <PamLoading label="Loading profile…" />}
        {!profileLoading && profileError && (
          <PamError message={profileError} onRetry={loadProfile} />
        )}
        {!profileLoading && !profileError && profile && (
          <>
            <ReadOnlyRow>
              <ReadOnlyLabel>Login email:</ReadOnlyLabel>
              <span>{profile.email ?? '—'}</span>
              {profile.emailVerified === false && <span>(unverified)</span>}
            </ReadOnlyRow>
            <InfoText>
              To change your login email or add another address, use the Email
              addresses section below.
            </InfoText>
            {profileSaveError && <PamError message={profileSaveError} />}
            <CrudForm
              key={profileFormKey}
              fields={PROFILE_FIELDS}
              initial={profileInitial(profile)}
              onSubmit={handleProfileSubmit}
              submitLabel="Save profile"
            />
          </>
        )}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>Email addresses</SectionTitle>
        {emailsLoading && <PamLoading label="Loading email addresses…" />}
        {!emailsLoading && emailsError && (
          <PamError message={emailsError} onRetry={loadEmails} />
        )}
        {!emailsLoading && !emailsError && (
          <>
            {addEmailMessage && <StatusMessage role="status">{addEmailMessage}</StatusMessage>}
            <CrudTable
              columns={emailColumns}
              items={emails}
              rowActions={emailRowActions}
              loading={false}
              caption="Email addresses"
              emptyMessage="No email addresses on file."
            />
            <CrudForm
              key={`email-form-${emails.length}`}
              fields={emailFields}
              onSubmit={handleAddEmail}
              submitLabel="Add email"
            />
          </>
        )}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>App passwords</SectionTitle>
        {newSecret && (
          <SecretCard role="alert">
            <SecretWarning>
              You will not be able to see this again — copy your new app
              password now and store it somewhere safe.
            </SecretWarning>
            <InfoText>Label: {newSecret.label}</InfoText>
            <SecretValueRow>
              <SecretCode>{newSecret.secret}</SecretCode>
              <CrudSecondaryButton type="button" onClick={handleCopySecret}>
                Copy
              </CrudSecondaryButton>
              <CrudSecondaryButton type="button" onClick={handleDismissSecret}>
                Dismiss
              </CrudSecondaryButton>
            </SecretValueRow>
            {copyStatus && <InfoText role="status">{copyStatus}</InfoText>}
          </SecretCard>
        )}
        {appPasswordsLoading && <PamLoading label="Loading app passwords…" />}
        {!appPasswordsLoading && appPasswordsError && (
          <PamError message={appPasswordsError} onRetry={loadAppPasswords} />
        )}
        {!appPasswordsLoading && !appPasswordsError && (
          <>
            {appPasswords.length === 0 ? (
              <PamEmpty message="No app passwords yet." />
            ) : (
              <CrudTable
                columns={appPasswordColumns}
                items={appPasswords}
                rowActions={appPasswordRowActions}
                loading={false}
                caption="App passwords"
              />
            )}
            {createAppPasswordError && <PamError message={createAppPasswordError} />}
            <CrudForm
              key={createAppPasswordFormKey}
              fields={appPasswordFields}
              onSubmit={handleCreateAppPassword}
              submitLabel="Create app password"
            />
          </>
        )}
      </SectionBlock>
    </PageStack>
  )
}
